<template>
  <div
    v-if="props.customisedDiscountProduct"
    :class="bem({ oos, promoTheme, selected })"
  >
    <div :class="bem('content')">
      <DynamicImg
        :key="props.customisedDiscountProduct.sku"
        :sku="props.customisedDiscountProduct.sku"
        :height="800"
        :width="800"
        bg="transparent"
        :view-angle="5"
        :class="bem('image')"
      />
      <div
        :class="bem('le-logo')"
      >
        <DatoCmsImage
          v-if="props.customisedDiscountProduct.product.limitedEditionLogo"
          :key="props.customisedDiscountProduct.product.limitedEditionLogo.responsiveImage.src"
          :data="props.customisedDiscountProduct.product.limitedEditionLogo.responsiveImage"
          :lazy-load="false"
        />
      </div>
      <div :class="bem('text-container')">
        <div :class="bem('title-container')">
          <div :class="bem('title')">
            <VisionHeading tag="h4">
              {{ props.customisedDiscountProduct.productName }}
            </VisionHeading>
          </div>
          <VisionHeading
            v-if="isPolarised"
            :class="bem('polarised')"
            size="h6"
            tag="h6"
          >
            {{ props.customisedDiscountProduct.lensChoice?.optionTech?.lensTechSelectorTitleOverride }}
          </VisionHeading>
        </div>
        <div :class="bem('prices')">
          <p
            v-if="newPrice"
            :class="bem('price')"
          >
            {{ $tcl(newPrice) }}
          </p>
          <p
            :class="
              bem('price', {
                strikethrough:
                  !!props.customisedDiscountProduct.state.discountSummary
                    .discountIsFree ||
                  !!props.customisedDiscountProduct.state.discountSummary
                    .discountPercentageOff ||
                  !!props.customisedDiscountProduct.state.discountSummary
                    .discountAmountOff,
              })
            "
          >
            {{ $tcl(props.customisedDiscountProduct.price) }}
          </p>
        </div>
      </div>
      <div
        v-if="selected"
        :class="bem('selected-indicator')"
      >
        <span :class="bem('selected-text')">
          Selected
        </span>
      </div>
      <div
        v-if="oos"
        :class="bem('oos-indicator')"
      >
        Sold Out
      </div>
    </div>
  </div>
  <div
    v-else-if="props.soldOut"
    :class="bem({ promoTheme, 'sold-out': true })"
  >
    <div :class="bem('content')">
      <DynamicImg
        key="renegades-rgf_Iblack-rgle_Nsmoke-rgi_Bgunmetal"
        sku="renegades-rgf_Iblack-rgle_Nsmoke-rgi_Bgunmetal"
        :height="800"
        :width="800"
        bg="transparent"
        :view-angle="5"
        :class="bem('image', {'default-product': true})"
      />
      <div
        :class="bem('oos-indicator')"
      >
        Sold Out
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Image as DatoCmsImage } from 'vue-datocms'

const props = defineProps<{
  customisedDiscountProduct: any
  selected: boolean
  promoTheme?: boolean
  soldOut?: boolean
}>()

const bem = useBEM('PromoSelectorModalItem')
const promoStore = usePromoStore()

const { $tcl } = useNuxtApp()

const oos = computed(() => {
  return props.soldOut || !props.customisedDiscountProduct.isInStock
})

const isPolarised = computed(() => {
  if (promoStore.showPolarisedTag) return ['kop', 'thp', 'stp'].some((value) =>
    props.customisedDiscountProduct.lensChoice?.optionTech?.tech.includes(value)
  )
  return false
  
})

const newPrice = computed(() => {
  if(props.customisedDiscountProduct.state.discountSummary.discountIsFree) {
    return ''
  } else if (props.customisedDiscountProduct.state.discountSummary.discountPercentageOff) {
    return $tcl(calculatedPricePercentOff.value)
  } else if (props.customisedDiscountProduct.state.discountSummary.discountAmountOff) {
    return $tcl(calculatedPriceAmountOff.value)
  }

  return ''
})

const calculatedPricePercentOff = computed(() => {
  return (
    props.customisedDiscountProduct.price -
    props.customisedDiscountProduct.price *
      props.customisedDiscountProduct.state.discountSummary
        .discountPercentageOff
  )
})

const calculatedPriceAmountOff = computed(() => {
  return (
    props.customisedDiscountProduct.price -
    props.customisedDiscountProduct.price *
      props.customisedDiscountProduct.state.discountSummary.discountAmountOff
  )
})
</script>

<style lang="postcss">
.PromoSelectorModalItem {
   @apply flex justify-between items-center text-left p-1.5 md:p-2.5 gap-4;
}

.PromoSelectorModalItem__oos-indicator {
  @apply absolute -bottom-px w-full h-4
  flex items-center justify-center
  py-4 rounded-b-lg
  font-bold uppercase text-white
  text-[10px] md:text-xxs;
  background: var(--promo-bg-tint, theme('colors.black'));
}

.PromoSelectorModalItem__content {
  @apply relative flex items-center;
}

.PromoSelectorModalItem__text-container {
  @apply absolute top-0 left-0 right-0 p-1.5 md:p-2.5
  flex justify-between items-start;
}

.PromoSelectorModalItem__price {
  @apply text-[10px] sm:text-xs;
}

.PromoSelectorModalItem__price--strikethrough {
  @apply line-through;
}

.PromoSelectorModalItem__title {
  .VisionHeading--size-h4 {
    @apply text-xs md:text-sm;
  }
}

.PromoSelectorModalItem__polarised {
  @apply mt-0.5 text-grey-500;
}

.PromoSelectorModalItem__description {
  @apply text-[12px] sm:text-sm;
}

.PromoSelectorModalItem__description,
.PromoSelectorModalItem__price {
  @apply font-bold text-black;
}

.PromoSelectorModalItem__prices {
  @apply flex gap-2;
}

.PromoSelectorModalItem__image {
  @apply w-full aspect-square;
}

.PromoSelectorModalItem__le-logo {
  @apply absolute bottom-0 right-1 w-[50%] max-w-[140px];
}

.PromoSelectorModalItem__selected-indicator {
  @apply absolute -bottom-px -left-px bg-white
  w-[72px] rounded-tr-lg flex justify-center pt-1;

  @screen md {
    @apply w-[96px] px-4 pl-2;
  }
}

.PromoSelectorModalItem__selected-text {
  @apply font-bold uppercase t-gradient
  text-[10px] md:text-xxs;
}

/* Promo Theme */
.PromoSelectorModalItem--promo-theme {
  --vision-tag-background-color: var(--promo-bg-tint);
  --vision-tag-text-color: var(--promo-highlight);
  .PromoSelectorModalItem__image {
    @apply rounded-lg;
    background: linear-gradient(to bottom, theme('colors.white') 0%, theme('colors.grey.DEFAULT') 100%)
  }

  .PromoSelectorModalItem__title {
    @apply promo-t-gradient-dark promo-t-gradient-animated;
  }

  .PromoSelectorModalItem__tag {
    @apply border border-transparent;
  }

  .PromoSelectorModalItem__selected-indicator {
    background: var(--promo-bg, theme('colors.white'))
  }

  .PromoSelectorModalItem__selected-text {
    @apply promo-t-gradient promo-t-gradient-animated;
  }

  &.PromoSelectorModalItem--selected {
    .PromoSelectorModalItem__image {
      @apply bg-grey-200
    }
  }
}

/* Sold Out Item */
.PromoSelectorModalItem--sold-out, .PromoSelectorModalItem--oos {
  .PromoSelectorModalItem__content {
    @apply rounded-lg bg-grey-500;
  }

  .PromoSelectorModalItem__image {
    @apply rounded-lg;
    background: transparent;
    filter: brightness(-50%) blur(6px);
  }
}
</style>
